/**
 * Shared sanitization helpers for PostHog and Sentry.
 *
 * Both tools call these before transmitting data to ensure
 * no PII leaks into observability platforms.
 *
 * See Part 22 §125 of the Tech Guidelines.
 */

import { PII_PATTERNS, SENSITIVE_KEYS } from './pii-patterns';

// ── String-level sanitization ────────────────────────────────────────

/**
 * Replaces detected PII patterns in a string with redaction tags.
 *
 * @example
 *   sanitizeString("Email: john@example.com")
 *   // → "Email: [EMAIL_REDACTED]"
 */
export function sanitizeString(input: string): string {
  let result = input;

  for (const [type, pattern] of Object.entries(PII_PATTERNS)) {
    // Reset regex lastIndex (patterns are global)
    pattern.lastIndex = 0;
    result = result.replace(pattern, `[${type}_REDACTED]`);
  }

  return result;
}

// ── Object-level sanitization ────────────────────────────────────────

/**
 * Sanitizes event properties for PostHog.
 * - Replaces detected PII in string values with redaction tags
 * - Removes sensitive keys entirely
 * - Leaves non-string values untouched
 */
export function sanitizeEventProps(props: Record<string, unknown>): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(props)) {
    // Skip sensitive keys entirely
    if (isSensitiveKey(key)) {
      continue;
    }

    if (typeof value === 'string') {
      sanitized[key] = sanitizeString(value);
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      sanitized[key] = sanitizeEventProps(value as Record<string, unknown>);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

/**
 * Sanitizes extra context for Sentry.
 * - Replaces detected PII in string values with redaction tags
 * - Truncates strings to max 1000 characters
 * - Removes sensitive keys entirely
 */
export function sanitizeSentryExtra(extra: Record<string, unknown>): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(extra)) {
    // Skip sensitive keys entirely
    if (isSensitiveKey(key)) {
      continue;
    }

    if (typeof value === 'string') {
      sanitized[key] = sanitizeString(value).slice(0, 1000);
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      sanitized[key] = sanitizeSentryExtra(value as Record<string, unknown>);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

// ── Helpers ──────────────────────────────────────────────────────────

function isSensitiveKey(key: string): boolean {
  const lowerKey = key.toLowerCase();
  return SENSITIVE_KEYS.some((sensitive) => lowerKey.includes(sensitive.toLowerCase()));
}
